import type Token from "markdown-it/lib/token.mjs";
import { restrictedTokens } from "@/components/controls/TextEditor/utils/restrictedMarkdown";
import { SLOT_FORMATS } from "../editor/slotFormats";
import { DocumentField, Inline, isField } from "./types";

// A multiline value's blocks as the composer reads them (contractsapi templates/rich_text.py).

// Text in one style.
export interface Run {
  text: string;
  bold: boolean;
  italic: boolean;
}

export type Line = Run[];

// A paragraph, or a list item's paragraph: its lines as the rep broke them. `marker`
// ("•", "3.") is drawn beside the first line of an item's first block only.
export interface RichBlock {
  lines: Line[];
  marker?: string;
  listKind?: "bullet" | "number";
}

// A paragraph holding only this is how the editor saves a blank line.
const BLANK_LINE = "​";
const BULLET = "•";

export const isEmptyLines = (block: RichBlock) =>
  block.lines.length === 1 && block.lines[0].length === 0;

const append = (line: Line, run: Run) => {
  const last = line[line.length - 1];
  if (last && last.bold === run.bold && last.italic === run.italic) {
    line[line.length - 1] = { ...last, text: last.text + run.text };
  } else if (run.text) {
    line.push(run);
  }
};

const linesOf = (children: Token[]): Line[] => {
  const lines: Line[] = [[]];
  let bold = 0;
  let italic = 0;
  for (const child of children) {
    if (child.type === "text" || child.type === "text_special") {
      append(lines[lines.length - 1], {
        text: child.content,
        bold: bold > 0,
        italic: italic > 0
      });
    } else if (child.type === "softbreak" || child.type === "hardbreak") {
      lines.push([]);
    } else if (child.type === "strong_open") bold += 1;
    else if (child.type === "strong_close") bold -= 1;
    else if (child.type === "em_open") italic += 1;
    else if (child.type === "em_close") italic -= 1;
  }
  const only = lines.length === 1 ? lines[0] : undefined;
  if (
    only &&
    only
      .map((run) => run.text)
      .join("")
      .trim() === BLANK_LINE
  ) {
    return [[]];
  }
  return lines;
};

// The value's blocks in drawing order; a nested list's items come after the item holding them.
export const parseRichText = (markdown: string): RichBlock[] => {
  const blocks: RichBlock[] = [];
  // Open lists, innermost last: their kind and the next item's number.
  const lists: { kind: "bullet" | "number"; next: number }[] = [];
  const items: { marker: string; kind: "bullet" | "number"; drawn: boolean }[] =
    [];
  for (const token of restrictedTokens(markdown, SLOT_FORMATS)) {
    if (
      token.type === "bullet_list_open" ||
      token.type === "ordered_list_open"
    ) {
      const ordered = token.type === "ordered_list_open";
      lists.push({
        kind: ordered ? "number" : "bullet",
        next: ordered ? Number(token.attrGet("start") || 1) : 1
      });
    } else if (
      token.type === "bullet_list_close" ||
      token.type === "ordered_list_close"
    ) {
      lists.pop();
    } else if (token.type === "list_item_open") {
      // An item holding a list before any text of its own still shows its marker, on an empty line.
      const holder = items[items.length - 1];
      if (holder && !holder.drawn) {
        blocks.push({
          lines: [[]],
          marker: holder.marker,
          listKind: holder.kind
        });
        holder.drawn = true;
      }
      const list = lists[lists.length - 1];
      items.push({
        marker: list.kind === "bullet" ? BULLET : `${list.next}.`,
        kind: list.kind,
        drawn: false
      });
      list.next += 1;
    } else if (token.type === "list_item_close") {
      const item = items.pop();
      if (item && !item.drawn) {
        blocks.push({ lines: [[]], marker: item.marker, listKind: item.kind });
      }
    } else if (token.type === "inline") {
      const lines = linesOf(token.children ?? []);
      const item = items[items.length - 1];
      if (item) {
        blocks.push({
          lines,
          marker: item.drawn ? undefined : item.marker,
          listKind: item.kind
        });
        item.drawn = true;
      } else {
        blocks.push({ lines });
      }
    }
  }
  return blocks;
};

const lineText = (line: Line) => line.map((run) => run.text).join("");

// The text a value draws: no markers, a line break or a blank line one newline.
export const visibleText = (markdown: string) =>
  parseRichText(markdown)
    .map((block) => block.lines.map(lineText).join("\n"))
    .join("\n");

// The text a part ends with, for the full stop the text after it may drop.
export const endsWithStop = (part: Inline) =>
  isField(part) &&
  !!part.value &&
  (part.multiline ? visibleText(part.value) : part.value).endsWith(".");

// One line or more of a paragraph block, in order: the template's own parts and a value's
// first paragraph run on from them, or a value's block and any template text after it.
export type PieceEntry =
  | { part: Inline }
  | { field: DocumentField; block: RichBlock };

export interface Piece {
  // The value's block that starts the piece, when one does.
  block?: RichBlock;
  entries: PieceEntry[];
}

const hasContent = (entry: PieceEntry) =>
  !("part" in entry) ||
  typeof entry.part !== "string" ||
  entry.part.trim() !== "";

// A paragraph whose value holds a list or a blank line, as the PDF draws it: its own lines,
// paragraphs and list items one after another. Undefined when no value does.
export const expandValues = (parts: Inline[]): Piece[] | undefined => {
  const pieces: Piece[] = [{ entries: [] }];
  const last = () => pieces[pieces.length - 1];
  let expanded = false;
  // Text right after a value ending in a full stop drops its own.
  let stopped = false;
  for (const original of parts) {
    let part = original;
    if (!isField(part) || !part.multiline || !part.value) {
      if (stopped && typeof part === "string" && part.startsWith(".")) {
        part = part.slice(1);
      }
      last().entries.push({ part });
      stopped = endsWithStop(part);
      continue;
    }
    const field = part;
    const blocks = parseRichText(field.value as string);
    expanded =
      expanded || blocks.some((block) => block.listKind || isEmptyLines(block));
    blocks.forEach((block, index) => {
      if (
        index === 0 &&
        !block.listKind &&
        !isEmptyLines(block) &&
        last().block === undefined
      ) {
        last().entries.push({ field, block });
      } else {
        pieces.push({ block, entries: [{ field, block }] });
      }
    });
    // Template text after a list item or a blank line is a line of its own.
    const final = blocks[blocks.length - 1];
    if (final && (final.listKind || isEmptyLines(final))) {
      pieces.push({ entries: [] });
    }
    stopped = endsWithStop(field);
  }
  if (!expanded) return undefined;
  return pieces.filter(
    (piece) => piece.block !== undefined || piece.entries.some(hasContent)
  );
};

// Plain lines for where a value is drawn as strings: each item starts with its marker.
export const markedText = (markdown: string) =>
  parseRichText(markdown)
    .map((block) =>
      block.lines
        .map((line, index) =>
          index === 0 && block.marker
            ? `${block.marker} ${lineText(line)}`
            : lineText(line)
        )
        .join("\n")
    )
    .join("\n");
